import React, { useState, useEffect, useMemo, useCallback } from "react"
import { Table, Col, Row } from "reactstrap"
import api from "API/Api"
import { Link } from "react-router-dom"
import SweetAlert from "react-bootstrap-sweetalert"
import loader from "./../../assets/loading/loader.gif"
import { usePermissions } from "API/PermissionContext"
import BulkImportExport from "../../components/BulkImportExport"

const Plants = () => {
  const { canAdd, canEdit, canDelete, canExport, MODULE } = usePermissions()
  const MID = MODULE.PLANTS
  const [plants, setPlants] = useState([])
  const [dataLoaded, setDataLoaded] = useState(false)
  const [checkedItems, setCheckedItems] = useState({})
  const [parentChecked, setParentChecked] = useState(false)
  const [confirm_alert, setconfirm_alert] = useState(false)
  const [confirm_multiple_alert, setconfirm_multiple_alert] = useState(false)
  const [success_dlg, setsuccess_dlg] = useState(false)
  const [dynamic_title, setdynamic_title] = useState("")
  const [dynamic_description, setdynamic_description] = useState("")
  const [plantId, setPlantId] = useState("")
  const [search, setSearch] = useState("")

  const fetchData = useCallback(async () => {
    try {
      const resp = await api.get("plants")
      const d = Array.isArray(resp.data.data) ? resp.data.data : resp.data.data?.data || []
      setPlants(d)
    } catch (err) { console.error(err) }
    setDataLoaded(true)
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const handleDelete = async () => {
    try { await api.delete("delete-plant/" + plantId); fetchData() } catch (e) { console.error(e) }
  }
  const handleMultiDelete = async () => {
    const ids = Object.keys(checkedItems).filter(k => checkedItems[k])
    if (!ids.length) return
    try { await api.post("delete-multiple-plants", { items: ids }); setCheckedItems({}); setParentChecked(false); fetchData() } catch (e) { console.error(e) }
  }
  const handleCheckbox = id => {
    setCheckedItems(prev => {
      const n = { ...prev, [id]: !prev[id] }
      setParentChecked(plants.length > 0 && plants.every(p => n[p.id]))
      return n
    })
  }
  const handleParentCheckbox = () => {
    const n = !parentChecked; setParentChecked(n)
    const items = {}; plants.forEach(p => { items[p.id] = n }); setCheckedItems(items)
  }
  const selectedCount = Object.values(checkedItems).filter(Boolean).length

  const filtered = useMemo(() => {
    if (!search) return plants
    const s = search.toLowerCase()
    return plants.filter(p =>
      (p.name || "").toLowerCase().includes(s) ||
      (p.number || "").toLowerCase().includes(s) ||
      (p.village || "").toLowerCase().includes(s) ||
      (p.stateName || "").toLowerCase().includes(s) ||
      (p.districtName || "").toLowerCase().includes(s)
    )
  }, [plants, search])

  // Summary
  const totalPlants = plants.length
  const stateCount = new Set(plants.map(p => p.stateName).filter(Boolean)).size
  const summaryCards = [
    { label: "Total Plants", value: totalPlants, icon: "fa-industry", color: "#6366f1" },
    { label: "States", value: stateCount, icon: "fa-map-marker-alt", color: "#0ea5e9" },
  ]

  const [showFilter, setShowFilter] = useState(false)
  const [filterSearch, setFilterSearch] = useState({ name: "", number: "", village: "", district: "", state: "" })

  const removeFilter = () => {
    setFilterSearch({ name: "", number: "", village: "", district: "", state: "" })
    setShowFilter(!showFilter)
  }

  const uniqueStates = useMemo(() => [...new Set(plants.map(p => p.stateName).filter(Boolean))].sort(), [plants])
  const uniqueDistricts = useMemo(() => [...new Set(plants.map(p => p.districtName).filter(Boolean))].sort(), [plants])

  // CSV Export
  const exportCSV = () => {
    if (!filtered.length) return
    const headers = ["S.No", "Plant Name", "Number", "Village", "District", "State", "Plant Manager", "Production Manager"]
    const rows = filtered.map((p, i) => [
      i + 1, p.name, p.number, p.village, p.districtName || "", p.stateName || "", p.plantManager || "", p.productionManager || ""
    ])
    const csvContent = [headers, ...rows].map(r => r.map(v => `"${(v || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n")
    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a"); a.href = url; a.download = `plants_${new Date().toISOString().slice(0, 10)}.csv`
    a.click(); URL.revokeObjectURL(url)
  }

  return (
    <>
      <div className="dashBody">

        <div className="font-size-18 mb-0 dashHeader" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <h4 style={{ margin: 0 }}>Plants</h4>
            {canAdd(MID) && (
              <Link to="/add-plant" title="Add Plant"
                style={{ width: "32px", height: "32px", borderRadius: "4px", background: "#28a745", color: "#fff",
                  display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px", textDecoration: "none" }}>
                <i className="fas fa-plus"></i>
              </Link>
            )}
            {canDelete(MID) && (
              <button title={selectedCount > 0 ? `Delete ${selectedCount} selected` : "Select items to delete"}
                onClick={() => { if (selectedCount > 0) setconfirm_multiple_alert(true); else alert("Please select at least one plant to delete.") }}
                style={{ width: "32px", height: "32px", borderRadius: "4px", background: "#dc3545", color: "#fff",
                  border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px", cursor: "pointer", opacity: selectedCount > 0 ? 1 : 0.7 }}>
                <i className="fas fa-trash"></i>
              </button>
            )}
            <button title="Toggle Filter" onClick={removeFilter}
              style={{ width: "32px", height: "32px", borderRadius: "4px", background: "#0d6efd", color: "#fff",
                border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px", cursor: "pointer" }}>
              <i className="fas fa-filter"></i>
            </button>
            <BulkImportExport
              data={plants}
              columns={[
                { key: "name", label: "Plant Name", required: true },
                { key: "number", label: "Plant Number", required: true },
                { key: "company_id", label: "Company ID", required: true },
                { key: "village", label: "Village" },
                { key: "state", label: "State ID" },
                { key: "district", label: "District ID" },
                { key: "pincode", label: "Pincode" },
                { key: "plantManager", label: "Plant Manager" },
                { key: "productionManager", label: "Production Manager" },
              ]}
              sampleData={[{
                name: "Sample Plant", number: "P001", company_id: "1",
                village: "Village", state: "1", district: "1", pincode: "560001",
                plantManager: "John", productionManager: "Doe"
              }]}
              apiEndpoint="create-plant"
              entityName="Plants"
              onImportComplete={fetchData}
              showExport={canExport(MID)}
              showImport={canAdd(MID)}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <input type="text" className="form-control form-control-sm" placeholder="Search"
              style={{ width: "180px", borderRadius: "4px 0 0 4px" }}
              value={search} onChange={e => setSearch(e.target.value)} />
            <button className="btn btn-primary btn-sm" style={{ borderRadius: "0 4px 4px 0", height: "31px" }}>
              <i className="fas fa-search"></i>
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <Row className="mb-3 mt-2">
          {summaryCards.map((card, i) => (
            <Col md={3} sm={6} key={i}>
              <div style={{
                background: "#fff", borderRadius: "10px", padding: "14px 16px",
                borderLeft: `4px solid ${card.color}`, boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px"
              }}>
                <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: card.color + "15",
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className={`fas ${card.icon}`} style={{ color: card.color, fontSize: "16px" }}></i>
                </div>
                <div>
                  <div style={{ fontSize: "20px", fontWeight: "700", color: "#1e293b" }}>{card.value}</div>
                  <div style={{ fontSize: "11px", color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.5px" }}>{card.label}</div>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        <div className="table-responsive">
          <Table className="table mb-0 dairyTbl" id="plantsTbl">
            <thead>
              <tr>
                <th><input type="checkbox" checked={parentChecked} onChange={handleParentCheckbox} /></th>
                <th>S.NO</th>
                <th>Plant Name</th>
                <th>Number</th>
                <th>Village</th>
                <th>District</th>
                <th>State</th>
                <th>Plant Manager</th>
                <th>Production Manager</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {showFilter && (
                <tr>
                  <td></td><td></td>
                  <td><input type="text" className="form-control form-control-sm" placeholder="Name..."
                    value={filterSearch.name} onChange={e => setFilterSearch(s => ({ ...s, name: e.target.value }))} /></td>
                  <td><input type="text" className="form-control form-control-sm" placeholder="Number..."
                    value={filterSearch.number} onChange={e => setFilterSearch(s => ({ ...s, number: e.target.value }))} /></td>
                  <td><input type="text" className="form-control form-control-sm" placeholder="Village..."
                    value={filterSearch.village} onChange={e => setFilterSearch(s => ({ ...s, village: e.target.value }))} /></td>
                  <td>
                    <select className="form-control form-control-sm" value={filterSearch.district}
                      onChange={e => setFilterSearch(s => ({ ...s, district: e.target.value }))}>
                      <option value="">All</option>
                      {uniqueDistricts.map((d, i) => <option key={i} value={d}>{d}</option>)}
                    </select>
                  </td>
                  <td>
                    <select className="form-control form-control-sm" value={filterSearch.state}
                      onChange={e => setFilterSearch(s => ({ ...s, state: e.target.value }))}>
                      <option value="">All</option>
                      {uniqueStates.map((st, i) => <option key={i} value={st}>{st}</option>)}
                    </select>
                  </td>
                  <td></td><td></td><td></td>
                </tr>
              )}
              {!dataLoaded ? (
                <tr><td colSpan={10}><img className="loaderImg" src={loader} alt="Loading" /></td></tr>
              ) : filtered.length > 0 ? (
                filtered.map((plant, i) => (
                  <tr key={plant.id}>
                    <td><input type="checkbox" checked={!!checkedItems[plant.id]} onChange={() => handleCheckbox(plant.id)} /></td>
                    <td>{i + 1}</td>
                    <td style={{ fontWeight: "600" }}>{plant.name}</td>
                    <td>{plant.number}</td>
                    <td>{plant.village}</td>
                    <td>{plant.districtName || "-"}</td>
                    <td>{plant.stateName || "-"}</td>
                    <td>{plant.plantManager || "-"}</td>
                    <td>{plant.productionManager || "-"}</td>
                    <td>
                      <div style={{ display: "flex", gap: "4px" }}>
                        {canEdit(MID) && <Link title="Edit" to={"/edit-plant/" + plant.id}
                          style={{ width: "28px", height: "28px", borderRadius: "4px", background: "#0d6efd", color: "#fff",
                            display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "12px" }}>
                          <i className="fas fa-edit"></i></Link>}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={10} className="text-center py-4">
                  <i className="fas fa-industry fa-2x text-muted mb-2 d-block"></i>No plants found
                </td></tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {confirm_alert && (
        <SweetAlert title="Are you sure?" warning showCancel confirmBtnBsStyle="success" cancelBtnBsStyle="danger"
          onConfirm={() => { handleDelete(); setconfirm_alert(false); setsuccess_dlg(true); setdynamic_title("Deleted"); setdynamic_description("Plant deleted.") }}
          onCancel={() => setconfirm_alert(false)}>You won't be able to revert this!</SweetAlert>
      )}
      {confirm_multiple_alert && (
        <SweetAlert title={`Delete ${selectedCount} plants?`} warning showCancel confirmBtnBsStyle="success" cancelBtnBsStyle="danger"
          onConfirm={() => { handleMultiDelete(); setconfirm_multiple_alert(false); setsuccess_dlg(true); setdynamic_title("Deleted"); setdynamic_description("Plants deleted.") }}
          onCancel={() => setconfirm_multiple_alert(false)}>You won't be able to revert this!</SweetAlert>
      )}
      {success_dlg && (
        <SweetAlert success title={dynamic_title} onConfirm={() => setsuccess_dlg(false)}>{dynamic_description}</SweetAlert>
      )}
    </>
  )
}

export default Plants
